import { Box, Flex, Typography, Tooltip, DeprecatedIcon, Middot } from '@fullstory/components';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlayButton } from '../PlayButton';
import { ServerEventButton } from '../ServerEventButton';
import { FormattedMessage } from 'react-intl';
import css from './index.module.scss';
import { useHover } from '@fullstory/react-hooks';
import { UserOverlayExpandableListItem } from '../UserOverlayExpandableListItem';
import { fullstorySelectors } from '@fullstory/redux';
import { useSelector } from 'react-redux';
import { SessionListItemButton } from '../SessionListItemButton';
import { useOrgHistory } from '../../hooks';

export type PlatformIcon =
  | 'device-mobile'
  | 'device-desktop'
  | 'device-laptop'
  | 'device-tablet'
  | 'device-server'
  | 'device-unknown'
  | 'device-robot';

interface SessionListItemProps extends Omit<React.ComponentProps<typeof PlayButton>, 'isClip'> {
  title: string;
  eventCount: number;
  pageCount: number;
  length: string;
  location: string;
  hostOrAppName: string;
  hostOrAppNameTooltip?: string;
  platformIcon: PlatformIcon;
  platformName: string;
  browserOrOs: string;
  browserOrOsTooltip?: string;
  serverEventVars?: string;
  serverEventName?: string;
  serverIntegrationName?: string;
  onPlayClick: () => void;
  lastEventAt: Date | null;
}

export const SessionListItem: React.FunctionComponent<SessionListItemProps> = ({
  title,
  eventCount,
  pageCount,
  length,
  location,
  hostOrAppName,
  hostOrAppNameTooltip,
  platformIcon,
  platformName,
  isOnline,
  isWatched,
  to,
  browserOrOs,
  browserOrOsTooltip,
  serverEventName,
  serverEventVars,
  serverIntegrationName,
  onPlayClick,
  lastEventAt,
}) => {
  const { hovered: isHovered, hoverProps } = useHover();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const { orgHistory, retentionTimestamp } = useOrgHistory();
  const insideRetentionWindow =
    !orgHistory || !lastEventAt || lastEventAt >= new Date(retentionTimestamp);

  return (
    <Box data-component="SessionListItem" width="100%" height={48} style={{ position: 'relative' }}>
      <UserOverlayExpandableListItem isExpanded={isExpanded} serverEventVars={serverEventVars}>
        <Flex justifyContent="space-between">
          <Box paddingRight={8}>
            {to ? (
              <SessionListItemButton
                insideRetentionWindow={insideRetentionWindow}
                retentionWindowInMonths={orgHistory}
                orgId={orgId}
              >
                <PlayButton
                  isOnline={isOnline}
                  isClip={false}
                  isWatched={isWatched}
                  to={to}
                  onClick={onPlayClick}
                  disabled={!insideRetentionWindow}
                />
              </SessionListItemButton>
            ) : (
              <ServerEventButton
                iconProps={{ color: 'secondary' }}
                isHovered={isHovered}
                isExpanded={isExpanded}
                onClick={() => setIsExpanded(isExpaned => !isExpaned)}
                {...hoverProps}
              />
            )}
          </Box>
          <Box flexGrow={1} overflow="hidden">
            <Flex justifyContent="space-between">
              <Box marginRight={12}>
                {to && insideRetentionWindow ? (
                  <Link to={to}>
                    <Typography ellipsize variant="body3" color={isOnline ? 'green' : 'blue'}>
                      {title}
                    </Typography>
                  </Link>
                ) : (
                  <Typography ellipsize variant="body3">
                    {title}
                  </Typography>
                )}
              </Box>
              <Typography variant="body3" ellipsize className={css.locationName}>
                {location}
              </Typography>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center">
              <Typography color="neutrals-n7" variant="body3" ellipsize>
                {serverEventName ? (
                  serverEventName
                ) : (
                  <>
                    <FormattedMessage
                      defaultMessage="{count, plural, one {# Event} other {# Events}}"
                      id="qObapd"
                      values={{ count: eventCount }}
                    />
                    <Middot />
                    <FormattedMessage
                      defaultMessage="{count, plural, one {# Page} other {# Pages}}"
                      id="OTYkQy"
                      values={{ count: pageCount }}
                    />
                    <Middot />
                    {length}
                  </>
                )}
              </Typography>
              <Box marginLeft={12}>
                <Flex alignItems="center">
                  {hostOrAppName ? (
                    hostOrAppNameTooltip ? (
                      <Tooltip tooltipContent={hostOrAppNameTooltip}>
                        <Typography
                          color="neutrals-n7"
                          variant="body3"
                          ellipsize
                          className={css.platformInfoList}
                        >
                          <span className={css.url}>{hostOrAppName}</span> <Middot />
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        color="neutrals-n7"
                        variant="body3"
                        ellipsize
                        className={css.platformInfoList}
                      >
                        <span className={css.url}>{hostOrAppName}</span> <Middot />
                      </Typography>
                    )
                  ) : null}

                  {browserOrOsTooltip ? (
                    <Tooltip tooltipContent={browserOrOsTooltip}>
                      <Typography
                        color="neutrals-n7"
                        variant="body3"
                        ellipsize
                        className={css.platformInfoList}
                      >
                        {browserOrOs}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography
                      color="neutrals-n7"
                      variant="body3"
                      ellipsize
                      className={css.platformInfoList}
                    >
                      {serverIntegrationName ? serverIntegrationName : browserOrOs}
                    </Typography>
                  )}
                  <Tooltip tooltipContent={platformName}>
                    <Box marginLeft={12}>
                      <DeprecatedIcon name={platformIcon} color="neutrals-n6" />
                    </Box>
                  </Tooltip>
                </Flex>
              </Box>
            </Flex>
          </Box>
        </Flex>
      </UserOverlayExpandableListItem>
    </Box>
  );
};
